import { useEffect, useState } from 'react';
import { IoWarningOutline } from 'react-icons/io5';

import { QuestionDuplicateCheckEndpoint } from '@core/types/types.endpoint.question';
import { Button } from '@frontend/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@frontend/components/ui/sheet';
import { useDisclosure } from '@frontend/hooks/use-disclosure';
import { DuplicateQuestion } from '@frontend/shared/components/DuplicateQuestionButton/DuplicateQuestion';
import { useApi } from '@frontend/shared/hooks/shared.hook.useApi';

export interface DuplicateQuestionButtonProps {
  title: string;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

export function DuplicateQuestionButton({
  title,
  inputRef,
}: DuplicateQuestionButtonProps) {
  const [query, setQuery] = useState(title);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Determine if the input has focus
  useEffect(() => {
    if (inputRef.current !== document.activeElement && query !== title) {
      setQuery(title);
    }
  }, [inputRef, query, title]);

  const { data: duplicates, loading } = useApi<QuestionDuplicateCheckEndpoint>(
    '/question/duplicateCheck',
    { query },
    !!query,
  );

  return (
    <>
      <Button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }}
        variant="destructive"
        disabled={loading}
        aria-label="Potential duplicate found"
        className={!loading && !duplicates?.length ? 'hidden' : ''}
      >
        <IoWarningOutline className="h-5 w-5 mr-2" />
        {loading ? 'Checking for duplicates...' : 'Potential duplicate'}
      </Button>

      {!!duplicates?.length && (
        <Sheet open={isOpen} onOpenChange={onClose}>
          <SheetContent side="right" className="w-[400px] sm:w-[540px]">
            <SheetHeader>
              <SheetTitle>Potential Duplicates</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              {duplicates?.map((q) => (
                <DuplicateQuestion key={q.id} question={q} />
              ))}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
